import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import ShotEngine from "@/pages/ShotEngine";
import WelcomePage from "@/pages/WelcomePage";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to always send auth header if token exists
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("shots_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function AppContent() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const authProcessed = useRef(false);

  // Process auth callback if session_id in URL
  useEffect(() => {
    const processAuthCallback = async () => {
      const hash = window.location.hash;
      if (!hash.includes("session_id=")) return false;
      
      // Prevent double processing
      if (authProcessed.current) return true;
      authProcessed.current = true;
      
      setIsProcessingAuth(true);
      
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      if (!sessionIdMatch) {
        setIsProcessingAuth(false);
        return false;
      }
      
      const sessionId = sessionIdMatch[1];
      
      try {
        const response = await axios.post(`${API}/auth/session`, { 
          session_id: sessionId 
        });
        
        const userData = response.data.user;
        const token = response.data.token;
        
        // Store both user and token
        localStorage.setItem("shots_user", JSON.stringify(userData));
        localStorage.setItem("shots_token", token);
        setUser(userData);
        
        // Clear hash from URL
        window.history.replaceState(null, "", window.location.pathname);
        
      } catch (error) {
        console.error("Auth callback error:", error);
        localStorage.removeItem("shots_user");
        localStorage.removeItem("shots_token");
      }
      
      setIsProcessingAuth(false);
      setIsLoading(false);
      return true;
    };

    const checkExistingAuth = async () => {
      // First check localStorage
      const storedUser = localStorage.getItem("shots_user");
      const storedToken = localStorage.getItem("shots_token");
      
      if (storedUser && storedToken) {
        setUser(JSON.parse(storedUser));
        
        // Verify token is still valid with backend
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          localStorage.setItem("shots_user", JSON.stringify(response.data));
        } catch (error) {
          // Token expired or invalid
          setUser(null);
          localStorage.removeItem("shots_user");
          localStorage.removeItem("shots_token");
        }
      }
      
      setIsLoading(false);
    };

    const init = async () => {
      const wasAuthCallback = await processAuthCallback();
      if (!wasAuthCallback) {
        await checkExistingAuth();
      }
    };

    init();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem("shots_user");
    localStorage.removeItem("shots_token");
  }, []);

  // Show loading while processing auth or initial check
  if (isLoading || isProcessingAuth) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#262626] border-t-[#BEF264] rounded-full animate-spin mx-auto mb-4" />
          {isProcessingAuth && (
            <p className="font-mono text-[11px] text-[#737373] tracking-wider uppercase">
              Autenticando...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Not logged in - show welcome
  if (!user) {
    return <WelcomePage />;
  }

  // Logged in - show app
  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <ShotEngine 
            user={user} 
            onLogout={handleLogout}
          />
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="noise-bg">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

export default App;
